import axios from "axios";
import { store } from "Store/mainStore";
import { fetchContractListPayload } from "../types";
import OOT from "Constants/services/oot";

class DatamartServices {

  getState() {
    // Import your Redux store and selector
    const reduxtStore = store.getState();
    // Placeholder for demonstration:
    const email = reduxtStore.authSlice?.login?.mail || ""; // Replace with actual Redux state value
    const userId = reduxtStore.authSlice?.login?.id || ""; // Get user ID from Redux state
    const roleList = reduxtStore.userManagement?.loggedInUserRole || [];
    const isEsignUser = Array.isArray(roleList) && roleList.includes('esign_user');
    return { userId, email, isEsignUser };
  }

  // Fetch Contract summary
  fetchContractSummary = async (type: string) => {
    try {
      // contract/frame-agreement/summary
      const apiConfig: { method: string; url: string; data: undefined; customHeaders?: Record<string, string> } = {
        method: 'get',
        url: `/v2/contract/frame-agreement/summary`,
        data: undefined,
      };
      // Agreement approval (frame agreement) sends no requested-info header; only agreement templates do.
      if (type === "esign") {
        apiConfig.customHeaders = {
          'requested_info': "esign",
        }
      }
      const response = await this.genericDatamartAPI(apiConfig);
      // Mock the summary response
      // const summary = await new Promise((resolve) =>
      //   setTimeout(() => {
      //     const mockResponse = [
      //         { title: "Total count", value: Math.floor(Math.random() * 100) + 50, color: "grey" },
      //         { title: "Approved", value: Math.floor(Math.random() * 150) + 10, color: "green" },
      //         { title: "Pending", value: Math.floor(Math.random() * 130) + 5, color: "orange" },
      //         { title: "Rejected", value: Math.floor(Math.random() * 120) + 5, color: "red" },
      //         { title: "Conditional", value: Math.floor(Math.random() * 10) + 1, color: "blue" },
      //       ];
      //       resolve(mockResponse);
      //     }, 3000)
      //   );

      // return { status: true, data: summary, message: '' };
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Unknown error' };
    }
  }

  getAPIOptions = (fieldAllowedValues: Record<string, any>, mapArray: string) => {

    const mapOptions = !!fieldAllowedValues && !!fieldAllowedValues[mapArray] && fieldAllowedValues[mapArray].map((status: string) => ({
      id: status,
      key: status,
      value: status
        .split("_")                         // split by underscore
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each
        .join(" ")                          // join back with space
    }));

    return mapOptions;
  }

  // Fetch Contract List
  fetchContractList = async (params: fetchContractListPayload, type: string) => {
    const { offset, limit, order_by = '', filter = '' } = params;
    const { isEsignUser } = this.getState();
    try {
      const apiConfig = {
        method: 'get',
        url: `/v2/contract/frame-agreement?offset=${offset}&limit=${limit}&order_by=${encodeURIComponent(order_by)}&filter=${encodeURIComponent(filter)}&field_allowed_values=true&userData=true`,
        data: undefined,
        customHeaders: {
          requested_info: (type === "esign" || isEsignUser) ? "esign" : ""
        }
      };
      const response = await this.genericDatamartAPI(apiConfig);
      const field_allowed_values = !!response && response.data?.field_allowed_values;
      if(!!field_allowed_values){
          const optionsConfig = [
            { apiKey: "frameAgreementTypeList", storeKey: "frameAgreementTypeList" },
            { apiKey: "agreementTypeList", storeKey: "agreementTypeList" },
            { apiKey: "agreementSubTypeList", storeKey: "agreementSubTypeList" },
            { apiKey: "contractualSituationList", storeKey: "contractualSituationList" },
          ];
          // Check whether frameAgreementTypeList exists and having the Other option, 
          // if not then add the Other option to the list to avoid issues in dropdown component and to allow users to select Other type if their frame agreement type is not listed in dropdown
          if(!!field_allowed_values["frameAgreementTypeList"] && !field_allowed_values["frameAgreementTypeList"].includes("Others")){
            field_allowed_values["frameAgreementTypeList"] = [...field_allowed_values["frameAgreementTypeList"], "Others"];
          }
          // Check whether agreementTypeList exists and having the Other option, 
          // if not then add the Other option to the list to avoid issues in dropdown component and to allow users to select Other type if their agreement type is not listed in dropdown
          if(!!field_allowed_values["agreementTypeList"] && !field_allowed_values["agreementTypeList"].includes("Others")){
            field_allowed_values["agreementTypeList"] = [...field_allowed_values["agreementTypeList"], "Others"];
          }

          // Import setFrameAgreementDropdownList dynamically to avoid circular dependency
          const { setFrameAgreementDropdownList } = await import('../Store/ContractsSlice');
          optionsConfig.forEach(({ apiKey, storeKey }) => {
            const options = this.getAPIOptions(field_allowed_values, apiKey);
            if (options && options.length > 0) {
              store.dispatch(setFrameAgreementDropdownList({ key: storeKey, value: options }));
            }
          });
      }
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Unknown error' };
    }
  }


  fetchContractCount = async (params: { filter: string, headers: any }) => {
    console.log("^^^^^^")
    const { isEsignUser } = this.getState();
    const { filter, headers } = params;
    try {
      const customHeaders = { ...(headers || {}) };
      if (isEsignUser && !customHeaders.requested_info && !customHeaders['requested-info']) {
        customHeaders.requested_info = 'esign';
      }

      const hasOwnerLikeFilter = /\bowner\s+like\b/i.test(String(filter || ''));
      const countUrl = filter !== ''
        ? `/v2/contract/frame-agreement?filter=${encodeURIComponent(filter)}${hasOwnerLikeFilter ? '&userData=true' : ''}`
        : '/v2/contract/frame-agreement?userData=true';

      const apiConfig = {
        method: 'get',
        url: countUrl,
        data: undefined,
        customHeaders // Pass headers to get count
      };
      const response = await this.genericDatamartAPI(apiConfig);
      return response;
    } catch (error: any) {
      return {
        status: false,
        message: error.message || 'Unknown error',
        total_count: 0
      };
    }
  }

  // Define a method for deleting a contract
  deleteContract(contractId: number) {
    // return this.genericDatamartAPI({
    //   method: 'delete',
    //   url: `/v2/contracts/${contractId}`,
    //   data: undefined
    // });

    // mock delete response
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ status: true, message: 'Contract deleted successfully' });
      }, 1000);
    });
  }


  /**
  * A generic function to make API calls (GET, POST, PUT, DELETE) to the Datamart service.
  * Handles errors and displays appropriate error dialogs if the request fails.
  * 
  * @param {Object} param - The API request details.
  * @param {string} param.method - The HTTP method (e.g., GET, POST, PUT, DELETE).
  * @param {string} param.url - The endpoint URL (relative to the Datamart base URL).
  * @param {Object} param.data - The request payload.
  * @returns {Promise<Object>} A promise that resolves to the API response or an error object.
  */
  async genericDatamartAPI({ method, url, data = undefined, customHeaders = {}, baseUrl = process.env.REACT_APP_DATAMART }: { method: any; url: any; data?: any; customHeaders?: Record<string, string>; baseUrl?: string }) {
    try {
      // Get the userId from the Redux store
      const { userId } = this.getState();
      // Prepare the API request configuration
      const config: { method: any; url: string; data: any; headers: Record<string, string> } = {
        method: method,
        // url: `${process.env.DATAMART_BASE_URL}${url}`,
        url: `${baseUrl}${url}`,
        data: data,
        headers: {
          'Content-Type': 'application/json',
          "x-user-id": userId,
        }
      }

      // Normalize eSign header alias so both backend and proxies can read it.
      const normalizedHeaders = { ...customHeaders };
      if (normalizedHeaders['requested_info'] && !normalizedHeaders['requested-info']) {
        normalizedHeaders['requested-info'] = normalizedHeaders['requested_info'];
      }
      if (normalizedHeaders['requested-info'] && !normalizedHeaders['requested_info']) {
        normalizedHeaders['requested_info'] = normalizedHeaders['requested-info'];
      }

      // Handle other headers if provided
      if (normalizedHeaders && typeof normalizedHeaders === 'object' && Object.keys(normalizedHeaders).length > 0) {
        Object.keys(normalizedHeaders).forEach(key => {
          // if (customHeaders?.[key] && customHeaders?.[key].trim().length > 0) {
          //   const value = customHeaders?.[key];
          //   config.headers![key] = value; // Use the value from customHeaders
          // }
          const value = normalizedHeaders[key];
          // ✅ Fix: Don't check for trim() if value might not be a string
          if (value !== undefined && value !== null && value !== '') {
            config.headers[key] = String(value); // ✅ Ensure it's a string
          }
        });
      }

      // Make the API request using axios
      const res = await axios(config);
      // prepare the response format
      const response = {
        statusCode: res.status,
        statusText: res.statusText,
        status: res.data?.status,
        message: res.data?.message || '',
        data: res?.data?.data || [],
        total_count: res?.data?.total_count || 0,
        requestId: res?.data?.requestId || '',
      }
      return response;
    } catch (err: any) {
      console.error("Error in genericDatamartAPI:", err);
      // showResponseDialog(this.redX.dispatch, err?.response?.status, { msg: err.code, panel: { title: 'Failed to connect server', msg: err?.response?.data?.errorMessage ?? err?.message } });
      return { data: [], status: err?.response?.data?.status };
    }
  }

  async createFrameAgreement(data: any) {
    const { isEsignUser } = this.getState();
    const response = await this.genericDatamartAPI({
      method: 'post',
      url: '/v2/contract/frame-agreement',
      data: data,
      // baseUrl: "http://localhost:4000"
      customHeaders: isEsignUser ? { requested_info: 'esign' } : {}
    });
    return response
  }

  async getFrameAgreementDetailsById(contractId: string, requestedInfo = "frame-agreement") {
    const response = await this.genericDatamartAPI({
      method: 'get',
      url: `/v2/contract/frame-agreement/${contractId}`,
      data: null,
      customHeaders: requestedInfo ? { requested_info: requestedInfo } : {}
    });
    return response;
  }

  async uploadFrameAgreementFile(path: string, formData: any) {
    const { userId } = this.getState();
    // Use axios directly for multipart uploads so boundary is set automatically
    // Always include the requested_info header for agreement file uploads
    const headers: Record<string, string> = {
      'x-user-id': String(userId),
      'requested-info': 'esign',
      'requested_info': 'esign'
    };

    const url = `${process.env.REACT_APP_DATAMART}/file_upload?name=agreement&data-element=contract_files&path=${path}&destination-type=aws`;
    const resp = await axios({
      method: 'post',
      url,
      data: formData,
      headers
    });

    // Normalize response to match genericDatamartAPI format
    return {
      statusCode: resp.status,
      statusText: resp.statusText,
      status: resp.data?.status,
      message: resp.data?.message || '',
      data: resp.data?.data || [],
      total_count: resp.data?.total_count || 0,
      requestId: resp.data?.requestId || ''
    };
  }

  async addFrameAgreementFileDetails(agreementId: string | number, uploadedFiles: any) {
    try {
      const { isEsignUser } = this.getState();
      const response = await this.genericDatamartAPI({
        method: 'post',
        url: `/v2/contract/frame-agreement/${agreementId}/file-upload`,
        // baseUrl: 'http://localhost:4000',
        data: uploadedFiles,
        customHeaders: { requested_info: 'esign' }
      });
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Failed to upload file' };
    }
  }

  async revokeFrameAgreement(frameAgreementId: string) {
    return await this.genericDatamartAPI({
      method: 'patch',
      url: `/v2/contract/frame-agreement/${frameAgreementId}`,
      customHeaders: {
        action: 'revoke',
        requested_info: 'esign',
        'requested-info': 'esign'
      }
    });
  }

  async updateFrameAgreement(frameAgreementId: string, data: any, action = "", method: 'put' | 'patch' = 'put') {
    const config: any = {
      method,
      url: `/v2/contract/frame-agreement/${frameAgreementId}`,
      // baseUrl: 'http://localhost:4000',
      data: data
    };

    if (action) {
      config.customHeaders = {
        action: action
      };
    }

    
    // Ensure e-sign header is always sent for update calls on agreement templates
    if (!config.customHeaders) config.customHeaders = {};
    // include both variants to be tolerant of backend naming
    config.customHeaders.requested_info = 'esign';
    config.customHeaders['requested-info'] = 'esign';

    const response = await this.genericDatamartAPI(config);
    return response
  }

  /**
   * This method updates a contract hub agreement by sending a PUT request to the Datamart API.
   * It accepts the frame agreement ID, the data to be updated, and an optional action parameter.
   * The action parameter can be used to specify a custom header for the request.
   * @param frameAgreementId The ID of the frame agreement to update.
   * @param data The data to update the frame agreement with.
   * @param action Optional action to specify a custom header for the request.
   * @returns The response from the Datamart API.
   */
  async updateContractHubAgreement(frameAgreementId: string, data: any, files: File[] = []) {
    const { userId } = this.getState();
    if (files.length > 0) {
      // Multipart: JSON fields as a string part + binary file parts.
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      files.forEach((file) => {
        formData.append('files', file, file.name);
      });

      const headers: Record<string, string> = { 'x-user-id': userId };
      
      const res = await axios({
        method: 'put',
        url: `${process.env.REACT_APP_DATAMART}/v2/contract/hub/agreement/${frameAgreementId}`,
        headers,
        data: formData,
      });
      return res.data;
    }

    // No files — regular JSON.
    const config: any = {
      method: 'put',
      url: `/v2/contract/hub/agreement/${frameAgreementId}`,
      data: data
    };
    
    return await this.genericDatamartAPI(config);
  }


  async deleteFrameAgreementFile(fileId: string) {
    const response = await this.genericDatamartAPI({
      method: 'delete',
      url: `/file_delete?key=${fileId}`,
      baseUrl: `${process.env.REACT_APP_DATAMART}`,
      data: null
    });
    return response;
  }

  async downloadFrameAgreementFile(fileId: string) {
    const response = await this.genericDatamartAPI({
      method: 'get',
      url: `/file_download?key=${fileId}`,
      baseUrl: `${process.env.REACT_APP_DATAMART}`,
      data: null
    });
    return response;
  }


  ///notify departments - trigger workflow API via datamart
  async notifyDepartments(agreementId: string | number, departmentData: any) {
    try {
      const response = await this.genericDatamartAPI({
        method: 'post',
        url: `/workflow/notify-departments`,
        data: { "id": `${agreementId}`, "departments": departmentData }
        // data: { "id": `${agreementId}` }

      });
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Failed to notify departments' };
    }
  }

  ///notify departments - trigger workflow API via datamart
  async approverActions(agreementData: any) {
    try {
      const response = await this.genericDatamartAPI({
        method: 'post',
        url: `/workflow/approver-action`,
        data: agreementData

      });
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Failed to perform approver actions' };
    }
  }



  //  /**
  //  * Fetch external HTML content through proxy to bypass CORS restrictions
  //  * @param {string} externalUrl - The external URL to fetch
  //  * @param {string} contentType - The content type ('pdf' or 'html')
  //  * @returns {Promise<Object>} - Returns the content as blob (PDF) or text (HTML)
  //  */
  // async getEsignPreviewUrl(externalUrl: string, contentType: 'pdf' | 'html' = 'pdf', action = "fetch-document", fileId = "") {
  //   try {
  //     const { userId } = this.getState();

  //     const config: any = {
  //       method: 'get',
  //       url: `http://localhost:4000/v2/contract/esign?url=${encodeURIComponent(externalUrl)}&content_type=${contentType}`,
  //       headers: {
  //         "x-user-id": userId,
  //         'x-action': action
  //       }
  //     };

  //     // Set response type and accept header based on content type
  //     if (contentType === 'pdf') {
  //       config.responseType = 'blob';
  //       config.headers['Accept'] = 'application/pdf';
  //     } else {
  //       config.responseType = 'text';
  //       config.headers['Accept'] = 'text/html';
  //     }

  //     const response = await axios(config);

  //     return { 
  //       status: true, 
  //       data: response.data // Blob for PDF, string for HTML
  //     };
  //   } catch (error) {
  //     console.error("Error fetching content:", error);
  //     return { 
  //       status: false, 
  //       message: error.response?.data?.errorMessage || error.message || 'Failed to fetch content'
  //     };
  //   }
  // }

  /**
   * Fetch external HTML/PDF content through proxy to bypass CORS restrictions
   * @param {Object} params - The request parameters
   * @param {string} params.fileId - Optional file ID
   * @param {string} params.url - Optional external URL
   * @param {string} params.agreement_id - Optional agreement ID
   * @param {'pdf' | 'html'} params.contentType - The content type to fetch
   * @param {'fetch-document' | 'fetch-document-by-id' | 'fetch-document-by-agreement-id'} params.action - The action type
   * @returns {Promise<Object>} - Returns the content as blob (PDF) or text (HTML)
   */
  async getEsignPreviewUrl({
    fileId = "",
    url = "",
    contentType = 'pdf',
    agreement_id = "",
    action = 'fetch-document'
  }: {
    fileId?: string;
    url?: string;
    agreement_id?: string;
    contentType?: 'pdf' | 'html';
    action?: 'fetch-document' | 'agreement-status';
  }) {
    try {
      const { userId } = this.getState();

      // ✅ Build query parameters based on action type
      let paramIdentifier = '';
      if (fileId) {
        paramIdentifier = `file_id=${encodeURIComponent(fileId)}&content_type=${contentType}`
      } else {
        paramIdentifier = `url=${encodeURIComponent(url)}&content_type=${contentType}`
      }

      let queryParams = ''

      switch (action) {
        case 'fetch-document':
          queryParams = paramIdentifier;
          break;
        case 'agreement-status':
          queryParams = `agreement_id=${agreement_id}`;
          break;
        default:
          queryParams = ``;
          break;
      }

      const config: any = {
        method: 'get',
        url: `${process.env.REACT_APP_DATAMART}/v2/contract/esign?${queryParams}`,
        headers: {
          "x-user-id": userId,
          'x-action': action
        }
      };

      // agreement-status returns JSON. Document fetch keeps existing pdf/html behavior.
      if (action === 'agreement-status') {
        config.responseType = 'json';
        config.headers['Accept'] = 'application/json';
      } else if (contentType === 'pdf') {
        config.responseType = 'blob';
        config.headers['Accept'] = 'application/pdf';
      } else {
        config.responseType = 'text';
        config.headers['Accept'] = 'text/html';
      }

      // console.log('📞 Fetching esign content:', { action, contentType });
      const response = await axios(config);

      return {
        status: true,
        data: response.data // Blob for PDF, string for HTML
      };
    } catch (error: any) {
      console.error('❌ Error fetching content:', error);
      return {
        status: false,
        message: error.response?.data?.errorMessage || error.message || 'Failed to fetch content'
      };
    }
  }


  async esign_notification(
    esignData: any,
    operationType: string,
    headerType: 'operation' | 'action' | 'x-action' | 'both' = 'operation'
  ) {
    try {
      const headerConfig: Record<string, string> =
        headerType === 'both'
          ? { operation: operationType, 'x-action': operationType }
          : headerType === 'x-action'
          ? { 'x-action': operationType }
          : headerType === 'action'
          ? { action: operationType }
          : { operation: operationType };

      const response = await this.genericDatamartAPI({
        method: 'post',
        url: `/workflow/e-sign-notification`,
        data: esignData,
        customHeaders: headerConfig
      });
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Failed to perform e-sign notification' };
    }
  }

  async shareSignedAgreement(agreementId: string | number, recipients: string[]) {
    try {
      const response = await this.genericDatamartAPI({
        method: 'post',
        url: '/v2/contract/esign',
        data: {
          agreement_id: agreementId,
          recipients,
        },
        customHeaders: {
          'x-action': 'share-agreement'
        }
      });
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Failed to share signed agreement' };
    }
  }

  async generatePdfFromBackend(
    htmlContent: string,
    fileName: string
  ): Promise<File> {

    const response = await axios({
      method: 'post',
      url: `${process.env.REACT_APP_DATAMART}/v2/contract/esign`,
      headers: {
        'x-action': 'html-to-pdf'
      },
      data: { htmlContent, fileName },
      responseType: 'blob'
    });

    const pdfBlob = response.data;

    console.log("Blob size:", pdfBlob.size);

    if (!pdfBlob || pdfBlob.size < 1000) {
      throw new Error('Generated PDF is empty or invalid');
    }

    return new File([pdfBlob], fileName, {
      type: 'application/pdf'
    });
  }


  // ✅ Add method to check if users exist
  async checkUsersExist(emails: string[]) {
    try {
      const emailFilter = emails.map(email => `'${email}'`).join(',');
      const filter = `email in (${emailFilter})`;

      // Using OOT API to get users
      const response = await OOT.getAllUsers(filter, 100, 0, 'user');
      return response;
    } catch (error) {
      console.error('Error checking users:', error);
      return { status: false, data: [] };
    }
  }

  // ✅ Modified method to handle both create and edit operations
  async createEsignUsers(users: Array<{ email: string, fullName: string, type: string, account_type: string, account_number?: string }>, operation: 'add' | 'edit' = 'add') {
    try {
      const response = await OOT.addEditUser(users, operation, 'user', 'esign');
      return response;
    } catch (error: any) {
      console.error(`Error ${operation === 'add' ? 'creating' : 'updating'} e-sign users:`, error);
      return { status: false, message: error.message || `Failed to ${operation === 'add' ? 'create' : 'update'} users` };
    }
  }


   /// Load the agreement rules from workflow only once when the component mounts
  async getAgreementRules() {
    try {
      const response = await this.genericDatamartAPI({
        method: 'get',
        url: `/workflow/rules/agreement`
      });
      return response;
    } catch (error: any) {
      return { status: false, message: error.message || 'Failed to load agreement rules' };
    }
  }

  /**
   * Fetch file from S3 using Datamart API proxy to bypass CORS restrictions
   * @param fileId - The unique identifier of the file in S3.
   * @returns A promise that resolves to the file download response.
   */
  async downloadS3File(fileId: string) {
    const response = await this.genericDatamartAPI({
      method: 'get',
      url: `/file_download?key=${fileId}`,
      baseUrl: `${process.env.REACT_APP_DATAMART}`,
      data: null
    });
    return response;
  }

  /**
   * Delete file from S3 using Datamart API proxy
   * @param fileId - The unique identifier of the file in S3.
   * @returns A promise that resolves to the file deletion response.
   */
  async deleteS3File(fileId: string) {
    const response = await this.genericDatamartAPI({
      method: 'delete',
      url: `/file_delete?key=${fileId}`,
      baseUrl: `${process.env.REACT_APP_DATAMART}`
    });
    return response;
  }


}

const DATAMART_SERVICES = new DatamartServices();
export default DATAMART_SERVICES;